import React, { Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";

// 'Geist' family is loaded via Google Fonts CDN in public/index.html
// (single variable-font import 'non.geist' was unreliable: the woff2 filename
// 'Geist[wght].woff2' contains literal brackets that webpack mishandles).
// Per-weight static imports below remain — they expose 'Geist-Regular',
// 'Geist-Bold', etc. as legacy aliases used by other parts of the app.
import 'non.geist/font-faces/Geist-Black.css'
import 'non.geist/font-faces/Geist-Bold.css'
import 'non.geist/font-faces/Geist-Light.css'
import 'non.geist/font-faces/Geist-Medium.css'
import 'non.geist/font-faces/Geist-Regular.css'
import 'non.geist/font-faces/Geist-SemiBold.css'
import 'non.geist/font-faces/Geist-Thin.css'
import 'non.geist/font-faces/Geist-UltraBlack.css'
import 'non.geist/font-faces/Geist-UltraLight.css'

import Loading from "./components/Loading";
import LoadingSpinner from "./components/LoadingScreen";

const TestPage = React.lazy(() => import("./NotLogged/Test"));
const LandingPage = React.lazy(() => import("./NotLogged/LandingPage"));
const NotFoundPage = React.lazy(() => import("./components/NotFoundPage"));
const SignupForConsultation = React.lazy(() => import("./components/SignupForConsultation"));
const PreviewEntry = React.lazy(() => import("./preview/PreviewEntry"));
const GuidelineLoader = React.lazy(() => import("./containers/GuidelineLoader"));
const ConsultationGuidelineLoader = React.lazy(() => import("./containers/Consultation/GuidelineLoader.js"));
const TestingHarness = React.lazy(() => import("./containers/Testing"));
const ToS = React.lazy(() => import("./BoringPages/ToS"));
// const AllGuidelines = React.lazy(() => import("./NotLogged/AllGuidelines"));

const FullscreenImage = React.lazy(() => import("./components/FullscreenImage"))
const Contact = React.lazy(() => import("./NotLogged/Contact"));

let user = {};

function GuidelinePage() {
  let { id } = useParams();
  return (
    <Suspense fallback={<Loading />}>
      <Guideline id={id} />
    </Suspense>
  );
}

function QualityIndicatorsPage() {
  let { id, user_id } = useParams();
  return (
    <Suspense fallback={<Loading />}>
      <QualityIndicators id={id} user_id={user_id} />
    </Suspense>
  );
}

function TheTestPage() {
  let { url } = useParams();
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <TestPage/>
    </Suspense>
  );
}

function GuidelineLoaderPage() {
  const { voteId, userId, id } = useParams();
  const { pathname } = useLocation();
  const resource = pathname.split('/')[1];
  return (
    <Suspense fallback={<LoadingSpinner message="Lade Leitlinie & Abstimmung …" />}>
      <GuidelineLoader key={`${resource}:${voteId || id}:${userId || ''}`} />
    </Suspense>
  );
}

function ConsultationGuidelineLoaderPage() {
  return (
    <Suspense fallback={<LoadingSpinner message="Lade Leitlinie & Abstimmung …" />}>
      <ConsultationGuidelineLoader />
    </Suspense>
  );
}

function FullscreenImagePage() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <FullscreenImage />
    </Suspense>
  );
}

function SignupForConsultationPage() {
  let { consultation_id } = useParams();
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <SignupForConsultation consultationId={consultation_id} />
    </Suspense>
  );
}



const App = () => {
  // The editor preview renders the participant view inside its own memory
  // router (nothing it does may touch the address bar), and React Router
  // refuses nested routers, so it mounts beside the browser router.
  if (window.location.pathname === '/preview/abstimmung') {
    return (
      <Suspense fallback={<Loading />}>
        <PreviewEntry />
      </Suspense>
    );
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<Loading />}>
        <Routes>
          {/* Abstimmung routes */}
          <Route path="/abstimmung/:voteId/:userId" element={<GuidelineLoaderPage />} />
          <Route path="/abstimmung/:voteId/:userId/:chapterId" element={<GuidelineLoaderPage />} />
          <Route path="/abstimmung/:voteId/:userId/:chapterId/:elementId" element={<GuidelineLoaderPage />} />

          {/*Umfrage Updatebedaft routes */}
          <Route path="/umfrage_updatebedarf/:voteId/:userId" element={<GuidelineLoaderPage />} />
          <Route path="/umfrage_updatebedarf/:voteId/:userId/:chapterId" element={<GuidelineLoaderPage />} />
          <Route path="/umfrage_updatebedarf/:voteId/:userId/:chapterId/:elementId" element={<GuidelineLoaderPage />} />

          {/*Konsultation routes*/}
          <Route exact path="/consultation/:consultation_id/author" element={<SignupForConsultationPage />} />
          <Route path="/consultation/:voteId/author/:userId" element={<ConsultationGuidelineLoaderPage />} />
          <Route path="/consultation/:voteId/author/:userId/:chapterId" element={<ConsultationGuidelineLoaderPage />} />
          <Route path="/consultation/:voteId/author/:userId/:chapterId/:elementId" element={<ConsultationGuidelineLoaderPage />} />

          <Route path="/consultation/:voteId/fachgesellschaft/:userId" element={<ConsultationGuidelineLoaderPage />} />
          <Route path="/consultation/:voteId/fachgesellschaft/:userId/:chapterId" element={<ConsultationGuidelineLoaderPage />} />
          <Route path="/consultation/:voteId/fachgesellschaft/:userId/:chapterId/:elementId" element={<ConsultationGuidelineLoaderPage />} />
          
          {/* Keep existing guideline routes if you still need them */}
          <Route path="/leitlinie/:id/*" element={<GuidelineLoaderPage />} />
          <Route path="/stageleitlinie/:id/*" element={<GuidelineLoaderPage />} />
          <Route path="/cg/:id/*" element={<GuidelineLoaderPage />} />
          
          <Route path="/fullscreen-image" element={<FullscreenImagePage />} />

          {/* Test harness for Questions.js (LeitlinieNEW) */}
          <Route path="/testing" element={
            <Suspense fallback={<LoadingSpinner />}>
              <TestingHarness />
            </Suspense>
          } />

          {/* Commented routes can remain commented or be removed if not needed */}
          <Route path="/kontakt" element={<Contact />} /> */}

          <Route path="/test" element={<TheTestPage />} /> */}
          <Route path="/ToS" element={<ToS />} /> */}

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default App;
